class BmhComboSocialProof extends HTMLElement {
  connectedCallback() {
    this.life?.abort();
    this.life = new AbortController();
    const { signal } = this.life;
    document.querySelectorAll('[data-bmh-combo-reviews]').forEach(link => link.addEventListener('click', event => {
      const dialog = document.getElementById('BmhComboReviewsDialog');
      if (typeof dialog?.showDialog !== 'function') return;
      event.preventDefault(); dialog.showDialog();
    }, { signal }));
    this.track = this.querySelector('[data-social-track]');
    this.cards = [...this.querySelectorAll('[data-social-card]')];
    this.dots = [...this.querySelectorAll('[data-social-dot]')];
    this.prev = this.querySelector('[data-social-prev]');
    this.next = this.querySelector('[data-social-next]');
    this.status = this.querySelector('[data-social-status]');
    this.progress = this.querySelector('[data-social-progress]');
    this.frame = 0;
    this.target = null;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    const show = () => { this.classList.add('is-visible'); this.reveal?.disconnect(); };
    if (this.motion.matches || !('IntersectionObserver' in window)) show();
    else {
      this.reveal = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) show(); }, { rootMargin: '0px 0px 30px 0px', threshold: 0 });
      this.reveal.observe(this);
    }
    if (!this.track || !this.cards.length) return;
    this.measure = () => {
      this.offsets = this.cards.map(card => card.offsetLeft - this.cards[0].offsetLeft);
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.offsets = this.offsets.map(offset => Math.min(offset, max));
      this.target = null;
      this.update();
    };
    this.resize = new ResizeObserver(this.measure);
    this.resize.observe(this.track);
    this.measure();
    this.track.addEventListener('scroll', () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => { this.target = null; this.update(true); }, 140);
    }, { passive: true, signal });
    this.track.addEventListener('pointerdown', () => { this.target = null; }, { passive: true, signal });
    this.prev?.addEventListener('click', () => this.go((this.target ?? this.index) - 1), { signal });
    this.next?.addEventListener('click', () => this.go((this.target ?? this.index) + 1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i), { signal }));
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : (this.target ?? this.index) + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });
    this.motion.addEventListener('change', show, { signal });
    this.videos = [...this.querySelectorAll('[data-social-video]')];
    if (this.videos.length && 'IntersectionObserver' in window) {
      this.videoObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const video = entry.target;
          if (entry.intersectionRatio < .5 || document.hidden || this.motion.matches || navigator.connection?.saveData) { video.pause(); continue; }
          if (!video.dataset.loaded) {
            video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
            video.dataset.loaded = 'true'; video.load();
          }
          video.play().catch(() => {});
        }
      }, { threshold: [0, .5] });
      this.videos.forEach(video => this.videoObserver.observe(video));
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.videos.forEach(video => video.pause()); }, { signal });
    }
    this.addEventListener('shopify:block:select', event => {
      const index = this.cards.indexOf(event.target.closest('[data-social-card]'));
      if (index >= 0) this.go(index);
    }, { signal });
  }

  go(index) {
    const target = Math.max(0, Math.min(this.cards.length - 1, index));
    this.target = target;
    this.track.scrollTo({ left: this.offsets[target], behavior: this.motion.matches ? 'instant' : 'smooth' });
  }

  update(announce = false) {
    const left = this.track.scrollLeft;
    this.index = this.offsets.reduce((best, offset, i) => Math.abs(offset - left) < Math.abs(this.offsets[best] - left) ? i : best, 0);
    if (this.prev) this.prev.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.cards.length - 1;
    if (this.progress) this.progress.style.transform = `scaleX(${(this.index + 1) / this.cards.length})`;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    if (announce && this.status) this.status.textContent = this.dots[this.index]?.getAttribute('aria-label') || '';
  }

  disconnectedCallback() {
    this.life?.abort(); this.reveal?.disconnect(); this.resize?.disconnect(); this.videoObserver?.disconnect();
    cancelAnimationFrame(this.frame); clearTimeout(this.scrollTimer);
    this.videos?.forEach(video => video.pause());
  }
}

if (!customElements.get('bmh-combo-social-proof')) customElements.define('bmh-combo-social-proof', BmhComboSocialProof);
